/**
 * Генератор параметризованных команд для Browser Control
 *
 * Новый подход: вместо множества команд с description,
 * генерируем небольшое количество параметризованных команд с params schema.
 *
 * Преимущества:
 * - Сжатие данных (~10 команд вместо 100+)
 * - Структурированность (явные типы и ограничения)
 * - Масштабируемость (добавление авто не увеличивает количество команд)
 *
 * Два типа навигации:
 * 1. SPA Navigation (client-side) - навигация внутри виджета через HashRouter
 * 2. Website Navigation (server-side) - навигация по страницам сайта клиента
 */

import { config } from '../config/runtimeConfig.js';
import { CATALOG_PAGE_IDS } from '../utils/pageContextParser.js';

/**
 * Единственная страница, где отключение навигации действует, — страница чатов
 * витрины. Признака «чат вписан в вёрстку» для этого мало: блок с инлайновым
 * чатом может появиться и на другой странице, и там уводить посетителя как
 * раз нормально. Отключение просили ровно для страницы чатов.
 */
export const PINNED_CHAT_ROUTE = '/contact';

/**
 * Приколот ли чат прямо сейчас: и страница та самая, и блок с чатом на ней
 * действительно смонтирован (без него чат плавает окном и уводить можно).
 *
 * @param {string} route - Текущий SPA-route
 * @param {boolean} hasInlineHost - Зарегистрирован ли контейнер инлайн-чата
 * @returns {boolean}
 */
export function isChatPinned(route, hasInlineHost) {
  return hasInlineHost === true && route === PINNED_CHAT_ROUTE;
}

/**
 * Что агенту позволено, пока чат ПРИКОЛОТ к странице (`pinned`).
 *
 * На странице чатов витрины чат стоит блоком в вёрстке, а рядом — мокап
 * инбокса, который показывает написанное туда же (#1330). Уводить страницу
 * оттуда нельзя: посетитель нажал «отправить» и оказывался в каталоге, а
 * демонстрируемая фича оставалась позади. Ровно это и просили: навигацию
 * здесь не использовать.
 *
 * Список БЕЛЫЙ, а не чёрный, и намеренно: половина каталожных команд
 * (`set_filters`, `view_cars`, `set_sort`) уводит на `/cars` deep-link'ом из
 * любой страницы, так что перечислять «уводящие» — значит однажды забыть
 * новую. Здесь перечислено то, что заведомо оставляет посетителя на месте.
 */
export const PINNED_CHAT_COMMANDS = new Set([
  'notify_success_booking',
  'show_widget',
  'scroll_top',
  'scroll_bottom',
]);

/**
 * Основная функция генерации параметризованных команд
 *
 * @param {Array} cars - Список автомобилей из API
 * @param {string} currentRoute - Текущий маршрут
 * @param {Object} options - Дополнительные опции
 * @param {string} options.widgetMode - Режим виджета ('full' | 'chat-only' | 'catalog-only')
 * @param {Array} options.websitePages - Массив страниц сайта для website navigation
 * @param {Object|null} options.websitePage - Текущая серверная страница (Issue #741)
 * @param {boolean} [options.pinned] - Чат приколот к странице: команды,
 *   уводящие с неё, не выдаются (#1330). См. `PINNED_CHAT_COMMANDS`.
 * @returns {Array} Массив параметризованных команд
 */
export function generateCommands(cars, currentRoute, options = {}) {
  // Получаем параметры из options или config
  const widgetMode = options.widgetMode || config.WIDGET_MODE || 'full';
  const websitePages = options.websitePages || config.WEBSITE_PAGES || [];
  const websitePage = options.websitePage || null; // Issue #741: текущая server page
  const pinned = options.pinned === true;

  // Issue #741: catalog commands (filters, view_cars) run in place only on the catalog page.
  // Without a websitePage (standalone mode, no WEBSITE_PAGES) they are always available.
  const isOnCatalogPage =
    !websitePage || CATALOG_PAGE_IDS.includes(websitePage.id);

  // #982: Off the catalog server page (home/about/…) the widget opens a car or a filtered
  // grid in ONE deep-link navigation (commandExecutor._deepLinkToCatalog). #1411: advertise
  // that in EVERY widget mode — the dealer's non-catalog pages carry the chat-only embed, and
  // without these entries the agent had no params schema for them (it dropped the `filters`
  // wrapper, or improvised a `show_widget` card). All the deep-link needs is a catalog page
  // to land on; the local inventory is not involved, so a still-empty `cars` doesn't matter.
  const offCatalogCommands =
    !isOnCatalogPage && hasCatalogPage(websitePages)
      ? [
          generateOffCatalogViewCarsCommand(),
          generateOffCatalogSetFiltersCommand(),
        ]
      : [];

  if (!cars || cars.length === 0) {
    console.warn(
      '[CommandGenerator] No cars in inventory, generating minimal commands'
    );
    // Обе стороны нужны: закреплённый чат не выдаёт команд вовсе (ветка), а
    // на некаталожной странице дилера дип-линки обязаны быть в списке даже
    // при пустом инвентаре — они от него не зависят (#1411, main).
    if (pinned) return [];
    return [
      ...generateNavigationCommands(currentRoute, { widgetMode, websitePages }),
      ...offCatalogCommands,
    ];
  }

  console.log(
    `[CommandGenerator] Generating commands for ${
      cars.length
    } cars, mode=${widgetMode}, websitePages=${
      websitePages.length
    }, websitePage=${websitePage?.id || 'none'}`
  );

  const commands = [];

  // Собираем уникальные значения для enum
  const uniqueValues = extractUniqueValues(cars);
  const ranges = calculateRanges(cars);

  // 1. Навигация (зависит от режима виджета)
  commands.push(
    ...generateNavigationCommands(currentRoute, { widgetMode, websitePages })
  );

  // 2. Параметризованные команды для фильтрации и просмотра
  // Доступны только в режиме 'full' или 'catalog-only' И когда мы на странице каталога
  const hasCatalog = widgetMode === 'full' || widgetMode === 'catalog-only';

  if (
    hasCatalog &&
    isOnCatalogPage &&
    (currentRoute === '/cars' || currentRoute === '/')
  ) {
    // Одиночный фильтр
    commands.push(generateSingleFilterCommand(uniqueValues, ranges));

    // Пакетные фильтры
    commands.push(generateBatchFiltersCommand(uniqueValues, ranges));

    // Просмотр авто
    commands.push(generateViewCarsCommand(cars));

    // Очистка фильтров
    commands.push(generateClearFiltersCommand());

    // Сортировка
    commands.push(generateSetSortCommand());

    // Простые действия
    commands.push(...generateSimpleActions());

    // Toggle Available Only (hide sold/reserved cars)
    commands.push({
      id: 'toggle_available_only',
      params: {
        enabled: {
          type: 'boolean',
          description:
            'true = show only available cars (hide sold/reserved), false = show all cars',
        },
      },
    });
  } else {
    // #982: relaxed view_cars / set_filters off the catalog (empty on it) — see offCatalogCommands.
    commands.push(...offCatalogCommands);
  }

  console.log(
    `[CommandGenerator] Generated ${commands.length} parameterized commands`
  );

  // Отсев одной точкой на выходе, а не условиями по ветками выше: так ни одна
  // ветка (и ни одна будущая) не может добавить уводящую команду в обход.
  if (pinned) return commands.filter((c) => PINNED_CHAT_COMMANDS.has(c.id));

  return commands;
}

/**
 * Извлечение уникальных значений из инвентаря для enum
 */
function extractUniqueValues(cars) {
  return {
    makes: [...new Set(cars.map((c) => c.make).filter(Boolean))].sort(),
    models: [...new Set(cars.map((c) => c.model).filter(Boolean))].sort(),
    bodyStyles: [
      ...new Set(cars.map((c) => c.bodyStyle).filter(Boolean)),
    ].sort(),
    transmissions: [
      ...new Set(cars.map((c) => c.transmission).filter(Boolean)),
    ].sort(),
    fuelTypes: [...new Set(cars.map((c) => c.fuelType).filter(Boolean))].sort(),
    carIds: cars.map((c) => c.id),
  };
}

/**
 * Расчёт min/max диапазонов для численных параметров
 */
function calculateRanges(cars) {
  // Drop cars with no year (#1206) — one absent value would make Math.min/max return NaN
  // and hand the agent NaN year bounds. Mirrors the guard CarsPage uses for the slider.
  const years = cars
    .map((c) => c.year)
    .filter((y) => typeof y === 'number' && y > 0);
  const prices = cars.map((c) => c.price);
  const mileages = cars.map((c) => c.mileage);

  return {
    // …and if NO car has a year, spreading an empty array gives ±Infinity, so report null.
    year: years.length
      ? { min: Math.min(...years), max: Math.max(...years) }
      : { min: null, max: null },
    price: { min: Math.min(...prices), max: Math.max(...prices) },
    mileage: { min: Math.min(...mileages), max: Math.max(...mileages) },
  };
}

/**
 * Существует ли SPA-route `/book-test-drive` в ТЕКУЩЕМ mount'е (#1251).
 *
 * Route объявлен ровно в ОДНОМ месте — standalone demo SPA (`src/App.jsx`,
 * `<Route path="/book-test-drive" element={<BookTestDrivePage />} />`). Все
 * embed-точки виджета (`components/registry/Catalog.jsx`; до #1329 сюда же
 * входили шеллы `AppWidget` / `AppWidgetFlexible`) заканчивают таблицу route'ов
 * `<Route path="*" element={<Navigate to="/cars" replace />} />` и НЕ объявляют
 * `/book-test-drive` — там установка hash'а переключает его на один тик, после
 * чего wildcard возвращает `/cars`, и покупатель никакой формы не видит.
 *
 * Дискриминатор — `ENVIRONMENT_NAME === 'widget'`: `vite.config.widget.js`
 * зашивает `VITE_ENVIRONMENT_NAME: 'widget'` на build-time для всего
 * embed-бандла (его entry — `src/widget.jsx`), и `widget.jsx` кладёт то же
 * значение в `window.runtimeConfig`. Т.е. это ровно «мы в embed-шелле, здесь
 * этого route'а нет». Standalone build (`vite.config.js`) значение 'widget' не
 * ставит.
 *
 * Executor дополнительно проверяет факт приземления (см.
 * `commandExecutor.navigateToBookTestDrive`) — этот гейт убирает ложную
 * возможность, тот убирает ложный отчёт об успехе.
 */
function hasBookTestDriveRoute() {
  return config.ENVIRONMENT_NAME !== 'widget';
}

/**
 * Is there a catalog page the off-catalog deep-link can land on (#982, #1411)?
 * `_deepLinkToCatalog` resolves its target from the same WEBSITE_PAGES entry, so a site
 * without one gets no off-catalog view_cars / set_filters — they would have nowhere to go.
 */
function hasCatalogPage(websitePages) {
  return (websitePages || []).some((p) => CATALOG_PAGE_IDS.includes(p.id));
}

/**
 * Навигационные команды
 *
 * Два типа навигации:
 * 1. SPA Navigation - навигация внутри виджета (go_home, go_cars, go_about и т.д.)
 *    Доступна только в режиме 'full' (с витриной)
 *
 * 2. Website Navigation - навигация по сайту клиента (go_website_page)
 *    Доступна во всех режимах, если настроены websitePages
 *
 * @param {string} currentRoute - Текущий маршрут
 * @param {Object} options - Опции
 * @param {string} options.widgetMode - Режим виджета ('full' | 'chat-only' | 'catalog-only')
 * @param {Array} options.websitePages - Массив страниц сайта для website navigation
 */
function generateNavigationCommands(currentRoute, options = {}) {
  const { widgetMode = 'full', websitePages = [] } = options;
  const commands = [];

  // ===== SPA NAVIGATION (только для режима 'full' с витриной) =====
  const hasCatalog = widgetMode === 'full' || widgetMode === 'catalog-only';

  if (hasCatalog) {
    // #770: go_home, go_about, go_contact — duplicated by go_website_page when websitePages configured
    const hasWebsitePages = websitePages && websitePages.length > 0;
    if (!hasWebsitePages) {
      if (currentRoute !== '/') {
        commands.push({ id: 'go_home' });
      }
      if (currentRoute !== '/about') {
        commands.push({ id: 'go_about' });
      }
      if (currentRoute !== '/contact') {
        commands.push({ id: 'go_contact' });
      }
    }

    if (currentRoute !== '/cars') {
      commands.push({ id: 'go_cars' });
    }

    // Команда для бронирования тест-драйва — только там, где route существует
    // (#1251). Раньше здесь была проверка self-navigation, а не существования.
    if (hasBookTestDriveRoute() && currentRoute !== '/book-test-drive') {
      commands.push({
        id: 'go_book_test_drive',
        params: {
          carInfo: {
            type: 'string',
            optional: true,
            description:
              'Car information string in format "year make model" (e.g., "2020 BMW X5")',
          },
        },
      });
    }

    // Service Center navigation commands (SPA).
    // Роуты /service и /service/appointment регистрируются в App.jsx только при
    // ENABLE_SERVICE_CENTER, а catch-all роута нет — без флага агент уводил
    // пользователя на пустой экран. Тот же класс, что #1251: проверка была на
    // self-navigation, а не на существование роута (#1323).
    if (config.ENABLE_SERVICE_CENTER && currentRoute !== '/service') {
      commands.push({ id: 'go_service' });
    }

    if (
      config.ENABLE_SERVICE_CENTER &&
      currentRoute !== '/service/appointment'
    ) {
      commands.push({ id: 'go_service_appointment' });
    }
  }

  // go_back и go_back_cars доступны во всех режимах (#766): после go_website_page
  // происходит full page reload и виджет может реинициализироваться в chat-only режиме,
  // но пользователь должен иметь возможность вернуться назад с сохранением фильтров
  if (currentRoute.startsWith('/cars/')) {
    commands.push({ id: 'go_back_cars' });
  }

  commands.push({
    id: 'go_back',
    description:
      'Navigate back to the previous page with preserved state (filters, scroll position)',
  });

  // ===== WEBSITE NAVIGATION (доступна во всех режимах если настроены websitePages) =====
  // #982: the catalog (CATALOG_PAGE_IDS) is NOT a go_website_page target — it's reached via
  // go_cars / set_filters / view_cars (which deep-link from any page). Advertising it here let
  // the agent pick go_website_page("catalog") → a bare UNFILTERED catalog instead of filtering.
  // Exclude it so go_website_page only offers genuine informational pages.
  const navPages = (websitePages || []).filter(
    (p) => !CATALOG_PAGE_IDS.includes(p.id)
  );
  if (navPages.length > 0) {
    commands.push(generateWebsiteNavigationCommand(navPages));
  }

  // ===== ОБЩИЕ КОМАНДЫ (доступны всегда) =====

  // Команда для уведомления об успешном бронировании (доступна всегда)
  commands.push({
    id: 'notify_success_booking',
    params: {
      date: {
        type: 'string',
        description: 'Booking date in ISO format (e.g., "2025-11-15")',
      },
      time: {
        type: 'string',
        description: 'Booking time (e.g., "14:30")',
      },
      name: {
        type: 'string',
        description: 'Customer name',
      },
      phone: {
        type: 'string',
        description: 'Customer phone number',
      },
      bookingId: {
        type: 'string',
        description: 'Unique booking ID',
      },
      carInfo: {
        type: 'string',
        optional: true,
        description: 'Optional car information (e.g., "2020 BMW X5")',
      },
    },
  });

  // #1411: chat widgets are behind ENABLE_SHOW_WIDGET (off by default). Advertised
  // unconditionally, `show_widget` was the only "display" command in the off-catalog map,
  // and the agent used it to invent card types the chat cannot render instead of view_cars.
  if (config.ENABLE_SHOW_WIDGET) {
    commands.push({
      id: 'show_widget',
      params: {
        messageText: {
          type: 'string',
          optional: true,
          description: 'Optional text message to show above the widget',
        },
        widget: {
          type: 'object',
          description: 'Widget object with type, id, and data fields',
          required: true,
        },
      },
    });
  }

  return commands;
}

/**
 * Команда для навигации по сайту клиента (Website Navigation)
 *
 * В отличие от SPA команд, эта команда выполняет полную перезагрузку страницы
 * с переходом на внешний URL сайта клиента.
 *
 * @param {Array} websitePages - Массив страниц сайта
 * @returns {Object} Команда go_website_page
 */
function generateWebsiteNavigationCommand(websitePages) {
  // Собираем ID страниц для enum
  // API entity uses: id, label, path, description
  const pageIds = websitePages.map((p) => p.id || p.label).filter(Boolean);

  // Формируем описания страниц для агента
  const pageDescriptions = websitePages
    .map((p) => {
      const id = p.id || p.label;
      const desc = p.description || p.label || p.id;
      return `${id}: ${desc}`;
    })
    .join('; ');

  return {
    id: 'go_website_page',
    description: `Navigate to dealer website page. Available pages: ${pageDescriptions}`,
    params: {
      page: {
        type: 'string',
        description: 'Page ID to navigate to',
        enum: pageIds,
      },
    },
  };
}

/**
 * Команда для установки одного фильтра
 */
function generateSingleFilterCommand(uniqueValues, ranges) {
  return {
    id: 'set_filter',
    params: {
      filterType: {
        type: 'string',
        enum: [
          'make',
          'model',
          'price',
          'mileage',
          'bodyStyle',
          'transmission',
          'fuelType',
          'year',
          'offerId',
        ],
      },
      values: {
        type: 'array',
        optional: true,
        enum: [
          ...uniqueValues.makes,
          ...uniqueValues.models,
          ...uniqueValues.bodyStyles,
          ...uniqueValues.transmissions,
          ...uniqueValues.fuelTypes,
        ],
      },
      min: {
        type: 'number',
        optional: true,
        min: 0,
      },
      max: {
        type: 'number',
        optional: true,
        max: ranges.price.max,
      },
    },
  };
}

/**
 * Команда для установки множественных фильтров
 */
function generateBatchFiltersCommand(uniqueValues, ranges) {
  return {
    id: 'set_filters',
    params: {
      filters: {
        type: 'object',
        properties: {
          make: {
            type: 'array',
            optional: true,
            enum: uniqueValues.makes,
          },
          model: {
            type: 'array',
            optional: true,
            enum: uniqueValues.models,
          },
          bodyStyle: {
            type: 'array',
            optional: true,
            enum: uniqueValues.bodyStyles,
          },
          transmission: {
            type: 'array',
            optional: true,
            enum: uniqueValues.transmissions,
          },
          fuelType: {
            type: 'array',
            optional: true,
            enum: uniqueValues.fuelTypes,
          },
          price: {
            type: 'object',
            optional: true,
            properties: {
              min: {
                type: 'number',
                min: ranges.price.min,
                max: ranges.price.max,
              },
              max: {
                type: 'number',
                min: ranges.price.min,
                max: ranges.price.max,
              },
            },
          },
          mileage: {
            type: 'object',
            optional: true,
            properties: {
              min: {
                type: 'number',
                min: ranges.mileage.min,
                max: ranges.mileage.max,
              },
              max: {
                type: 'number',
                min: ranges.mileage.min,
                max: ranges.mileage.max,
              },
            },
          },
          year: {
            type: 'object',
            optional: true,
            properties: {
              min: {
                type: 'number',
                min: ranges.year.min,
                max: ranges.year.max,
              },
              max: {
                type: 'number',
                min: ranges.year.min,
                max: ranges.year.max,
              },
            },
          },
          availableOnly: {
            type: 'boolean',
            optional: true,
            description:
              'true = show only available cars (hide sold/reserved), false = show all',
          },
          offerId: {
            type: 'string',
            optional: true,
            description:
              'Hidden filter for specific offer ID (e.g., "317207336-1") - agent use only, not visible in UI',
          },
          offerIds: {
            type: 'array',
            optional: true,
            description:
              'Hidden multi-ID filter for a set of offer IDs (e.g., ["1234", "1266"]) - agent use only. Use when Meilisearch returns a set of candidates that cannot be reproduced by attribute filters alone. Shown to the customer as a removable chip — always pair with offerIdsLabel.',
          },
          offerIdsLabel: {
            type: 'string',
            optional: true,
            description:
              'Chip text for the offerId/offerIds selection, shown to the customer above the catalog grid (e.g. "family SUVs under €25k"). Short plain words in the customer\'s language, no IDs. Ignored unless offerId/offerIds is present in the same call.',
          },
        },
      },
    },
  };
}

/**
 * Команда для просмотра автомобилей
 */
function generateViewCarsCommand(cars) {
  return {
    id: 'view_cars',
    params: {
      offerId: {
        type: 'string',
        description:
          'Offer ID to view (e.g., "534162-1"). Must be from visibleOfferIds array received in filter results.',
        enum: cars.map((c) => c.id),
      },
    },
  };
}

/**
 * #982: Relaxed view_cars for OFF-catalog pages — opens a car detail page directly via a
 * one-step deep-link (commandExecutor._deepLinkToCatalog). No offerId enum: the local widget
 * inventory may not be loaded here, and the id comes from search_car_inventory.
 */
function generateOffCatalogViewCarsCommand() {
  return {
    id: 'view_cars',
    description:
      "Open a specific car's detail page directly from any page (one navigation). Use the offerId from search_car_inventory results.",
    params: {
      offerId: {
        type: 'string',
        description:
          'Offer ID to open (e.g. "1216"), from search_car_inventory results.',
      },
    },
  };
}

/**
 * #982: Relaxed set_filters for OFF-catalog pages — applies filters and lands on the catalog
 * grid in one deep-link navigation. Same filter shape as the on-catalog command but without
 * value enums / ranges (inventory may not be loaded off the catalog page).
 */
function generateOffCatalogSetFiltersCommand() {
  return {
    id: 'set_filters',
    description:
      'Apply catalog filters and show matching cars, from any page (one navigation). The filters go inside params.filters; an empty filters object opens the full catalog.',
    params: {
      filters: {
        type: 'object',
        properties: {
          make: { type: 'array', optional: true },
          model: { type: 'array', optional: true },
          bodyStyle: { type: 'array', optional: true },
          transmission: { type: 'array', optional: true },
          fuelType: { type: 'array', optional: true },
          price: {
            type: 'object',
            optional: true,
            properties: { min: { type: 'number' }, max: { type: 'number' } },
          },
          mileage: {
            type: 'object',
            optional: true,
            properties: { min: { type: 'number' }, max: { type: 'number' } },
          },
          year: {
            type: 'object',
            optional: true,
            properties: { min: { type: 'number' }, max: { type: 'number' } },
          },
          availableOnly: { type: 'boolean', optional: true },
          offerIds: {
            type: 'array',
            optional: true,
            description:
              'Set of offer IDs from search_car_inventory (e.g. ["1216","1221"]).',
          },
        },
      },
    },
  };
}

/**
 * Команда для очистки фильтров
 */
function generateClearFiltersCommand() {
  return {
    id: 'clear_filters',
    params: {
      filterTypes: {
        type: 'array',
        optional: true,
        enum: [
          'make',
          'model',
          'price',
          'mileage',
          'bodyStyle',
          'transmission',
          'fuelType',
          'year',
          'availableOnly',
          'all',
        ],
      },
    },
  };
}

/**
 * Команда для изменения сортировки
 */
function generateSetSortCommand() {
  return {
    id: 'set_sort',
    params: {
      sortBy: {
        type: 'string',
        enum: [
          'price-asc',
          'price-desc',
          'year-desc',
          'year-asc',
          'mileage-asc',
          'mileage-desc',
        ],
      },
    },
  };
}

/**
 * Простые действия без параметров
 */
function generateSimpleActions() {
  return [{ id: 'scroll_top' }, { id: 'scroll_bottom' }];
}

export default generateCommands;
